// ============================================================================
// src/components/block/liveStreamManagment/ProductionControlRoom_LibraryPicker.tsx
//
// Icône « Library » du mediaboard (FieldMedia de FilePickerButton) : choisir une image, un son ou une vidéo
// parmi les médias du compte (espace disque du créateur) au lieu du disque dur
// local.
//
// Image et son : comme la vidéo, rien n'est copié. Leur rendu public sur le
// serveur média (`image.webp`, `audio.aac`) est remis tel quel à `onFile` :
// l'url distante est gardée dans le slot et dans la sauvegarde automatique, sans
// fichier téléversé — avant le 2026-10-22, chaque choix dans « Library » recopiait
// le média dans `uploadFiles/controlRoomConfigs/`, débité une 2e fois du quota.
// Un média plus ancien, sans rendu public, retombe sur l'ancien chemin : le
// fichier est téléchargé (endpoint de l'API) puis rendu en data URL.
//
// Vidéo : rien n'est téléchargé. Le serveur média ne la publie qu'en HLS ; c'est
// cette url (.m3u8) qui est remise à `onFile`, gardée telle quelle dans le slot
// (storeMediaSource) et lue par hls.js (setVideoSource).
// ============================================================================

import React, { useState } from 'react';
import { createPortal } from 'react-dom';

import { Media } from '../../../dataTypes/mediaManagment/types';
import { getMediaById } from '../../../backendServices/mediaManagment/api';
import { getVideoList } from '../../../backendServices/mediaManagment/api_video';
import { useTypedSelector } from '../../../store/useTypeSelector';
import { API_URLS, WWW_URLS } from '../../../backendServices/urls';
import { hlsUrlOfVideo } from '../../../dataTypes/liveStreamManagment/utils_videoSource';
import MediaSelector from '../mediaManagment/MediaSelector';

export type LibraryKind = 'image' | 'sound' | 'video';

/** Type de bibliothèque correspondant à l'attribut `accept` d'un sélecteur de fichier. */
export function libraryKindFromAccept(accept: string): LibraryKind | null {
  if (accept.startsWith('image/')) return 'image';
  if (accept.startsWith('audio/')) return 'sound';
  if (accept.startsWith('video/')) return 'video';
  return null;
}

const KIND_INFOS: Record<LibraryKind, { apiType: string; fileUrl: string | null; rendu: string | null; renduMime: string | null }> = {
  image: { apiType: 'Image', fileUrl: API_URLS.getImageFile, rendu: 'images/image.webp', renduMime: 'image/webp' },
  sound: { apiType: 'Sound', fileUrl: API_URLS.getSoundFile, rendu: 'sounds/audio.aac',  renduMime: 'audio/aac'  },
  video: { apiType: 'Video', fileUrl: null,                  rendu: null,                renduMime: null         },
};

/** Le rendu public existe-t-il ? Le serveur média répond en CORS (`*`). */
const renduExiste = async (url: string): Promise<boolean> => {
  try { return (await fetch(url, { method: 'HEAD' })).ok; } catch { return false; }
};

// ─── Téléchargement ───────────────────────────────────────────────────────────

const downloadAsDataUrl = (url: string, onProgress: (pct: number) => void): Promise<{ dataUrl: string; mime: string }> =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', url, true);
    xhr.responseType = 'blob';
    xhr.onprogress = e => { if (e.lengthComputable && e.total > 0) onProgress(Math.round(e.loaded / e.total * 100)); };
    xhr.onload = () => {
      if (xhr.status !== 200) { reject(new Error(xhr.status === 404 ? 'Fichier introuvable sur le serveur' : `Erreur HTTP ${xhr.status}`)); return; }
      const blob = xhr.response as Blob;
      const reader = new FileReader();
      reader.onload = ev => resolve({ dataUrl: ev.target?.result as string, mime: blob.type });
      reader.onerror = () => reject(new Error('Lecture du fichier impossible'));
      reader.readAsDataURL(blob);
    };
    xhr.onerror = () => reject(new Error('Erreur réseau'));
    xhr.send();
  });

const EXT_BY_MIME: Record<string, string> = {
  'image/jpeg': 'jpg', 'image/png': 'png', 'image/gif': 'gif', 'image/webp': 'webp', 'image/svg+xml': 'svg',
  'audio/mpeg': 'mp3', 'audio/wav': 'wav', 'audio/x-wav': 'wav', 'audio/ogg': 'ogg', 'audio/mp4': 'm4a', 'audio/aac': 'aac', 'audio/flac': 'flac',
  'video/mp4': 'mp4', 'video/webm': 'webm', 'video/quicktime': 'mov', 'video/x-matroska': 'mkv',
};

/**
 * Nom remis à `onFile` : il sert de libellé (sans extension) et de nom d'origine.
 * Le nom téléversé d'abord ; à défaut le titre du média, avec une extension
 * tirée du type rendu par le serveur pour que la suite le range correctement.
 */
const fileNameOf = (media: Media, mime: string): string => {
  if (media.originalFile) return media.originalFile;
  const ext = EXT_BY_MIME[mime];
  return ext ? `${media.name}.${ext}` : media.name;
};

// ─── Choix dans le sélecteur de médias ───────────────────────────────────────
//
// Le choix se fait dans le MediaSelector de l'application (catégories, tri,
// recherche, création de média), restreint au type du slot. Il ne rend qu'un
// id : la fiche complète est relue pour le nom, le publicRef et l'état.

interface LibraryPickerProps {
  kind: LibraryKind;
  onClose: () => void;
  onFile: (dataUrl: string, rawName: string) => void;
}

export const LibraryPicker: React.FC<LibraryPickerProps> = ({ kind, onClose, onFile }) => {
  const infos = KIND_INFOS[kind];
  const accentColor = '#60a5fa';

  const adminId = useTypedSelector(state => state.authenticationReducer.authentication.admin?.id);

  const [pct,   setPct]   = useState<number | null>(null);
  const [error, setError] = useState('');

  const pick = async (mediaId: number) => {
    if (pct !== null) return;
    setError('');
    setPct(0);
    try {
      if (!infos.fileUrl) {
        // La vidéo est relue dans la LISTE des vidéos : GET /api/videos/{id} ne
        // renvoie ni publicRef ni state — l'url HLS devenait /undefined/videos/…
        const list = await getVideoList(false, adminId);
        const media = list.code === 1 ? list.payload.find(v => v.id === mediaId) : undefined;
        if (!media) throw new Error('fiche du média introuvable');
        if (!media.publicRef) throw new Error('vidéo sans référence publique');
        // Une vidéo pas encore transcodée n'a pas de rendu HLS à lire.
        if (media.state === -1) throw new Error('vidéo encore en cours de transcodage');
        onFile(hlsUrlOfVideo(WWW_URLS.MediaServer, media.publicRef, media.videoFile), media.originalFile || media.name);
        onClose();
        return;
      }

      const response = await getMediaById(mediaId);
      if (response.code !== 1) throw new Error('fiche du média introuvable');
      const media = response.payload;

      if (infos.rendu && infos.renduMime && media.publicRef) {
        const rendu = `${WWW_URLS.MediaServer}${media.publicRef}/${infos.rendu}`;
        if (await renduExiste(rendu)) {
          onFile(rendu, fileNameOf(media, infos.renduMime));
          onClose();
          return;
        }
      }

      const url = infos.fileUrl.replace('{id}', `${media.id}`);
      const { dataUrl, mime } = await downloadAsDataUrl(url, setPct);
      onFile(dataUrl, fileNameOf(media, mime));
      onClose();
    } catch (e: any) {
      setError(e?.message ?? 'erreur inconnue');
      setPct(null);
    }
  };

  return createPortal(
    <>
      <MediaSelector
        originalFilter={[infos.apiType]}
        mediaTypeNames={[infos.apiType]}
        onSelect={id => { void pick(id); }}
        onCancel={() => { if (pct === null) onClose(); }}
      />
      {(pct !== null || error) && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{
            minWidth: 260, padding: 16, borderRadius: 10, background: '#111827', color: '#fff',
            border: `1px solid ${accentColor}60`, display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center',
          }}>
            {error
              ? <>
                  <span style={{ fontSize: 13, color: '#f87171' }}>{error}</span>
                  <button type="button" onClick={() => setError('')}
                    style={{ background: 'none', border: `1px solid ${accentColor}`, color: accentColor, borderRadius: 6, padding: '4px 14px', cursor: 'pointer' }}>OK</button>
                </>
              : <span style={{ fontSize: 13, color: accentColor }}>Chargement… {pct}%</span>}
          </div>
        </div>
      )}
    </>,
    document.body,
  );
};
